import { formatCustomDate } from "helpers/dateTools";
import {
  buildDefaultTaxBreakdown,
  calcCreLicenseTotals,
  calcPaymentAmounts,
  calcTaxBreakdownTotals,
  calcTotalToBill,
  clampQuantity,
  clampUnitPrice,
  fillPaymentGroups,
  resolvePaymentNameById,
} from "views/GlobalInvoices/invoiceTotals";
import { globalInvoiceTypes } from "./types";

const {
  GET_GLOBAL_INVOICE_PAYMENTS_TYPES,
  GET_GLOBAL_INVOICE_LICENSES,
  CREATE_GLOBAL_INVOICE_BREAKDOWN,
  GET_GLOBAL_INVOICE,
  SET_GLOBAL_INVOICE_FILTERS,
  SET_GLOBAL_INVOICE_UNIT_PRICES,
  SET_GLOBAL_INVOICE_QUANTITY,
  RESTORE_INITIAL,
} = globalInvoiceTypes;

const INITIAL_STATE = {
  globalInvoice: {},
  paymentTypes: [],
  creLicenses: [],
  station: null,
  startDate: formatCustomDate(new Date(), "-"),
  endDate: formatCustomDate(new Date(), "-"),
};

/**
 * El backend manda la llave del metodo de pago; el nombre legible sale del
 * catalogo que ya se descargo por separado.
 */
/**
 * Deja una tasa lista para pintarse: nombres resueltos y totales recalculados
 * a partir de sus metodos de pago.
 */
const normalizeTaxBreakdown = (taxBreakdown = {}, paymentTypes = []) => {
  const resolvedPayments = Object.entries(taxBreakdown?.payments || {}).reduce(
    (acc, [key, payment]) => {
      acc[key] = { ...payment, name: resolvePaymentNameById(payment?.idPayment, paymentTypes) };
      return acc;
    },
    {},
  );

  // Los 5 metodos de contado siempre se muestran, con los que no trajo el
  // backend en cero para que el usuario los pueda capturar en Manual.
  const payments = fillPaymentGroups(resolvedPayments, paymentTypes);

  return {
    ...taxBreakdown,
    ...calcTaxBreakdownTotals(payments),
    payments,
  };
};

/**
 * El codigo postal no viaja en la respuesta del timbrado global: sale del
 * catalogo de /stations/cre_licenses, que la vista ya descargo aparte.
 */
const resolvePostalCode = (creLicense, creLicenses = []) =>
  creLicenses.find((license) => license?.creLicense === creLicense)
    ?.fiscalPostalCode;

/**
 * Cada permiso de la respuesta, con sus tasas normalizadas y sus totales.
 */
const normalizeCreLicense = (license = {}, paymentTypes = [], creLicenses = []) => {
  // Un permiso sin actividad en el periodo llega con taxBreakdown vacio; se
  // sintetizan las tasas estandar para que igual se pueda desplegar y
  // capturar en Manual, en vez de mostrar un desglose en blanco.
  const rawTaxBreakdown = license?.taxBreakdown?.length
    ? license.taxBreakdown
    : buildDefaultTaxBreakdown(paymentTypes);

  const taxBreakdown = rawTaxBreakdown.map((tax) =>
    normalizeTaxBreakdown(tax, paymentTypes),
  );

  return {
    ...license,
    // Si algun dia el backend lo manda, el suyo gana.
    postalCode:
      license?.postalCode ?? resolvePostalCode(license?.creLicense, creLicenses),
    ...calcCreLicenseTotals(taxBreakdown),
    taxBreakdown,
  };
};

/**
 * Aplica un cambio manual a un renglon y propaga la cadena hacia arriba:
 * renglon -> tasa -> permiso -> Total Acumulado.
 *
 * La direccion del renglon es la terna permiso + tasa + metodo de pago: con
 * varios permisos en pantalla la tasa sola ya no lo identifica.
 */
const applyPaymentChange = (state, payload = {}, changes = {}) => {
  const { creLicense, taxRate, paymentKey } = payload;
  const licenses = state.globalInvoice?.pendingByCreLicense;
  if (!Array.isArray(licenses)) return state;

  const licenseIndex = licenses.findIndex(
    (license) => license?.creLicense === creLicense,
  );
  if (licenseIndex === -1) return state;

  const taxBreakdown = licenses[licenseIndex]?.taxBreakdown || [];
  const taxIndex = taxBreakdown.findIndex((tax) => tax?.taxRate === taxRate);
  if (taxIndex === -1) return state;

  const payment = taxBreakdown[taxIndex]?.payments?.[paymentKey];
  if (!payment) return state;

  const edited = { ...payment, ...changes };
  const payments = {
    ...taxBreakdown[taxIndex].payments,
    [paymentKey]: {
      ...edited,
      ...calcPaymentAmounts({
        unitPrice: edited.unitPrice,
        quantity: edited.quantity,
        taxRate,
      }),
    },
  };

  const nextTaxBreakdown = [...taxBreakdown];
  nextTaxBreakdown[taxIndex] = {
    ...taxBreakdown[taxIndex],
    ...calcTaxBreakdownTotals(payments),
    payments,
  };

  const nextLicenses = [...licenses];
  nextLicenses[licenseIndex] = {
    ...licenses[licenseIndex],
    ...calcCreLicenseTotals(nextTaxBreakdown),
    taxBreakdown: nextTaxBreakdown,
  };

  return {
    ...state,
    globalInvoice: {
      ...state.globalInvoice,
      pendingByCreLicense: nextLicenses,
      totalToBill: calcTotalToBill(nextLicenses),
    },
  };
};

export const globalInvoicesReducer = (
  state = INITIAL_STATE,
  { type, payload },
) => {
  switch (type) {
    case SET_GLOBAL_INVOICE_FILTERS:
      // Cambiar de planta o de fechas invalida el resultado que se este
      // mostrando: si no, se timbraria el periodo nuevo con pagos viejos.
      if (["station", "startDate", "endDate"].includes(payload.key)) {
        return {
          ...state,
          [payload.key]: payload.data,
          globalInvoice: {},
        };
      }
      return {
        ...state,
        [payload.key]: payload.data,
      };

    case GET_GLOBAL_INVOICE_PAYMENTS_TYPES:
      return {
        ...state,
        paymentTypes: payload,
      };

    case GET_GLOBAL_INVOICE_LICENSES:
      return {
        ...state,
        creLicenses: payload,
      };

    case GET_GLOBAL_INVOICE: {
      if (!payload) return { ...state, globalInvoice: {} };

      // Se construye una copia: la respuesta no se muta.
      const pendingByCreLicense = (payload?.pendingByCreLicense || []).map(
        (license) =>
          normalizeCreLicense(license, state.paymentTypes, state.creLicenses),
      );

      return {
        ...state,
        globalInvoice: {
          ...payload,
          // Marca de esta busqueda. La vista la usa para descartar las
          // casillas Manual al traer resultados nuevos: el backend dejo de
          // mandar `date` en la V4 y el objeto completo cambia en cada
          // edicion manual, asi que ninguno de los dos sirve de disparador.
          fetchedAt: Date.now(),
          pendingByCreLicense,
          // El unico total del pie que no manda el backend, porque cambia
          // cada vez que el usuario edita un renglon en manual.
          totalToBill: calcTotalToBill(pendingByCreLicense),
        },
      };
    }

    case SET_GLOBAL_INVOICE_UNIT_PRICES:
      return applyPaymentChange(state, payload, {
        unitPrice: clampUnitPrice(payload?.unitPrice),
      });

    case SET_GLOBAL_INVOICE_QUANTITY:
      return applyPaymentChange(state, payload, {
        quantity: clampQuantity(payload?.quantity),
      });

    // El POST solo encola (CRM-3109); no hay resultado por permiso/pago que
    // reducir aqui. Se limpia la busqueda porque lo seleccionado ya se mando
    // a la cola -el avance real se sigue en "Estatus Facturacion Global".
    case CREATE_GLOBAL_INVOICE_BREAKDOWN:
      return {
        ...state,
        globalInvoice: {},
      };

    case RESTORE_INITIAL:
      return {
        ...INITIAL_STATE,
        paymentTypes: state.paymentTypes,
        creLicenses: state.creLicenses,
        startDate: state.startDate,
        endDate: state.endDate,
        station: state.station,
      };

    default:
      return state;
  }
};
